<script setup lang="ts">
import { useFieldTexts, useValidationRules } from '@/composables';
import { useValidationErrorStore } from '@/stores/validationErrorStore';
import {
    useAttrs, computed, ref, onMounted,
} from 'vue';
import type { VTextField } from 'vuetify/components';
import { generateInputId } from '@/utils/idUtils';

type PropsType = {
    modelValue?: string | number | null;
    label?: string;
    placeholder?: string;
    variant?: VTextField['$props']['variant'];
    density?: VTextField['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    fieldName?: string;
    panelIndex?: number;
    tabId?: string;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: false,
    loading: false,
    isLabelTranslated: true,
    showRequiredVisual: true,
    dataType: 'string',
});

const emit = defineEmits<{
    'update:modelValue': [value: string | number];
    'click:clear': [];
    'click:append': [];
    'click:prepend': [];
    'click:appendInner': [];
    'click:prependInner': [];
    focus: [event: FocusEvent];
    blur: [event: FocusEvent];
    input: [value: string | number];
    change: [value: string | number];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { getLabel, getPlaceholder } = useFieldTexts({
    label: props.label,
    isTranslated: props.isLabelTranslated,
    placeholder: props.placeholder,
});

const { rules, isRequired } = useValidationRules(attrs);

const validationStore = useValidationErrorStore();
const resolvedFieldName = props.fieldName || props.label || 'field';

const inputValidation = validationStore.currentFormId
    ? validationStore.registerInput(resolvedFieldName, props.panelIndex ?? null, props.tabId ?? null)
    : null;

const computedId = computed(() => {
    return inputValidation?.inputId || generateInputId('textfield');
});

const textFieldRef = ref<VTextField | null>(null);

onMounted(() => {
    const input = textFieldRef.value?.$el?.querySelector('input');
    if (input) {
        input.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VTextField
        ref="textFieldRef"
        :id="computedId"
        :model-value="modelValue"
        :label="getLabel"
        :placeholder="getPlaceholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :name="name"
        :class="[$attrs.class, { required: isRequired && showRequiredVisual }]"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @update:model-value="emit('update:modelValue', $event)"
        @click:clear="emit('click:clear')"
        @click:append="emit('click:append')"
        @click:prepend="emit('click:prepend')"
        @click:append-inner="emit('click:appendInner')"
        @click:prepend-inner="emit('click:prependInner')"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @input="emit('input', $event)"
        @change="emit('change', $event)"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots['prepend-inner']"
            #prepend-inner
        >
            <slot name="prepend-inner" />
        </template>

        <template
            v-if="$slots['append-inner']"
            #append-inner
        >
            <slot name="append-inner" />
        </template>

        <template
            v-if="$slots.label"
            #label
        >
            <slot name="label" />
        </template>

        <template
            v-if="$slots.message"
            #message="{ message }"
        >
            <slot
                name="message"
                :message="message"
            />
        </template>

        <template
            v-if="$slots.details"
            #details
        >
            <slot name="details" />
        </template>
    </VTextField>
</template>
